import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CartAdapter } from '@spartacus/cart/base/core';
import { Cart, CART_NORMALIZER } from '@spartacus/cart/base/root';
import {
  ConverterService,
  InterceptorUtil,
  Occ,
  OCC_CART_ID_CURRENT,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_CURRENT,
  OccEndpointsService,
  USE_CLIENT_TOKEN,
} from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { map, pluck } from 'rxjs/operators';

import { KN_BR_CUSTOMER_CURRENT } from '../../constants/api.endpoints.constant';
import { KnBrCustomerContextService } from '../store/kn-br-customer-context/kn-br-customer-context.service';
import { KnBrCartContextAdapter } from '../store/kn-br-cart-context/kn-br-cart-context.adapter';

@Injectable()
export class KnBrOccCartAdapter implements CartAdapter {
  customerId: string;
  constructor(
    protected http: HttpClient,
    protected occEndpointsService: OccEndpointsService,
    protected converterService: ConverterService,
    private customerContextService: KnBrCustomerContextService,
    private cartContextAdapter: KnBrCartContextAdapter
  ) {
    this.customerContextService.get$.subscribe((response) => {
      this.customerId = response && response.customerNumber ? response.customerNumber : null;
    });
  }

  private resolveCustomerId(userId?: string): string {
    if (this.customerId) {
      return this.customerId;
    }

    if (userId && userId !== OCC_USER_ID_ANONYMOUS && userId !== OCC_USER_ID_CURRENT) {
      return userId;
    }

    return KN_BR_CUSTOMER_CURRENT;
  }

  public loadAll(userId: string): Observable<Cart[]> {
    // If caller passed an explicit customer id (not the OCC 'current' or 'anonymous' tokens),
    // prefer it. Otherwise fall back to adapter's `this.customerId` or the default KN_BR_CUSTOMER_CURRENT.
    const customerId =
      userId && userId !== OCC_USER_ID_CURRENT && userId !== OCC_USER_ID_ANONYMOUS
        ? userId
        : this.customerId
        ? this.customerId
        : KN_BR_CUSTOMER_CURRENT;

    return this.http

      .get<Occ.CartList>(this.occEndpointsService.buildUrl('carts', { urlParams: { customerId } }))
      .pipe(pluck('carts'), this.converterService.pipeableMany(CART_NORMALIZER));
  }

  public load(userId: string, cartId: string): Observable<Cart> {
    if (cartId === OCC_CART_ID_CURRENT) {
      // Spartacus asks for the 'current' cart whenever its active cart is cleared; without a cart context there is nothing to load.
      if (!this.cartContextAdapter.get()) {
        return of(null);
      }
      return this.loadAll(userId).pipe(
        map((carts) => {
          if (carts) {
            const activeCart = carts.find((cart) => {
              return cart.saveTime === undefined;
            });
            return activeCart;
          } else {
            return null;
          }
        })
      );
    } else {
      if (window.location.href.indexOf('/login') > -1) {
        localStorage.removeItem('spartacus⚿carlisle-sales⚿cart');
        return of({});
      }

      const customerId =
        userId && userId !== OCC_USER_ID_CURRENT && userId !== OCC_USER_ID_ANONYMOUS
          ? userId
          : this.customerId
          ? this.customerId
          : KN_BR_CUSTOMER_CURRENT;
      return this.http
        .get<Occ.Cart>(this.occEndpointsService.buildUrl('cart', { urlParams: { customerId, cartId } }))
        .pipe(this.converterService.pipeable(CART_NORMALIZER));
    }
  }

  create(userId: string, oldCartId?: string, toMergeCartGuid?: string): Observable<Cart> {
    const customerId = this.resolveCustomerId(userId);

    let params: { toMergeCartGuid?: string; oldCartId?: string } = {};

    if (oldCartId) {
      params = { oldCartId };
    }
    if (toMergeCartGuid) {
      params.toMergeCartGuid = toMergeCartGuid;
    }

    return this.http
      .post<Occ.Cart>(
        this.occEndpointsService.buildUrl('createCart', {
          urlParams: { customerId },
          queryParams: params,
        }),
        {}
      )
      .pipe(this.converterService.pipeable(CART_NORMALIZER));
  }
  // Need to Discuss
  save(userId: string, cartId: string, saveCartName?: string, saveCartDescription?: string): Observable<Cart> {
    return;
  }

  delete(userId: string, cartId: string): Observable<{}> {
    let headers = new HttpHeaders();
    if (userId === OCC_USER_ID_ANONYMOUS) {
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
    }
    return this.http.delete<{}>(
      this.occEndpointsService.buildUrl('deleteCart', {
        urlParams: { customerId: this.resolveCustomerId(userId), cartId },
      }),
      { headers }
    );
  }

  addEmail(userId: string, cartId: string, email: string): Observable<{}> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);

    const httpParams: HttpParams = new HttpParams().set('email', email);

    const url = this.occEndpointsService.buildUrl('addEmail', {
      urlParams: {
        customerId: this.resolveCustomerId(userId),
        cartId,
      },
    });

    return this.http.put(url, httpParams, { headers });
  }
}
